import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function LandingPage() {
  const navigate = useNavigate();
  const { lang } = useLang();

  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      borderBottom: '1px solid #e5e7eb',
      padding: '20px 0'
    },
    headerContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '0 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#2563eb'
    },
    logoText: {
      fontSize: '20px',
      fontWeight: '500'
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '32px'
    },
    btnPrimary: {
      backgroundColor: '#111827',
      color: '#ffffff',
      padding: '10px 24px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '15px'
    },
    btnSecondary: {
      backgroundColor: 'transparent',
      color: '#374151',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '15px'
    },
    hero: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '96px 24px 128px'
    },
    heroContent: {
      maxWidth: '800px'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#2563eb',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '32px'
    },
    h1: {
      fontSize: '56px',
      fontWeight: '700',
      lineHeight: '1.1',
      color: '#111827',
      marginBottom: '24px'
    },
    heroDesc: {
      fontSize: '20px',
      color: '#6b7280',
      lineHeight: '1.6',
      marginBottom: '40px'
    },
    btnGroup: {
      display: 'flex',
      gap: '16px'
    },
    btnLarge: {
      padding: '16px 32px',
      fontSize: '18px',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    btnOutline: {
      backgroundColor: 'transparent',
      color: '#374151',
      border: '2px solid #e5e7eb'
    },
    featuresSection: {
      backgroundColor: '#f9fafb',
      padding: '96px 24px'
    },
    container: {
      maxWidth: '1280px',
      margin: '0 auto'
    },
    sectionTitle: {
      textAlign: 'center',
      marginBottom: '64px'
    },
    h2: {
      fontSize: '36px',
      fontWeight: '700',
      color: '#111827',
      marginBottom: '16px'
    },
    sectionDesc: {
      fontSize: '18px',
      color: '#6b7280',
      maxWidth: '700px',
      margin: '0 auto'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '32px',
      maxWidth: '1100px',
      margin: '0 auto'
    },
    featureCard: {
      backgroundColor: '#ffffff',
      padding: '32px',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    featureIcon: {
      fontSize: '36px',
      marginBottom: '16px'
    },
    featureTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#111827',
      marginBottom: '12px'
    },
    featureDesc: {
      fontSize: '15px',
      color: '#6b7280',
      lineHeight: '1.6'
    },
    stepsSection: {
      padding: '96px 24px'
    },
    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '48px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    stepCard: {
      textAlign: 'center'
    },
    stepNumber: {
      fontSize: '120px',
      fontWeight: '700',
      color: '#f3f4f6',
      lineHeight: '1',
      marginBottom: '24px'
    },
    stepTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#111827',
      marginBottom: '16px'
    },
    stepDesc: {
      fontSize: '15px',
      color: '#6b7280',
      lineHeight: '1.6'
    },
    ctaSection: {
      backgroundColor: '#111827',
      padding: '96px 24px',
      textAlign: 'center'
    },
    ctaTitle: {
      fontSize: '36px',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: '16px'
    },
    ctaDesc: {
      fontSize: '20px',
      color: '#d1d5db',
      maxWidth: '700px',
      margin: '0 auto 40px'
    },
    btnWhite: {
      backgroundColor: '#ffffff',
      color: '#111827'
    },
    footer: {
      borderTop: '1px solid #e5e7eb',
      padding: '32px 24px'
    },
    footerContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    footerText: {
      fontSize: '14px',
      color: '#6b7280'
    }
  };

  const features = [
    {
      icon: '📅',
      title: lang === 'fr' ? 'Gestion du calendrier' : 'Calendar Management',
      desc: lang === 'fr' 
        ? 'Les auditeurs gèrent leurs disponibilités et périodes bloquées en temps réel.'
        : 'Auditors manage their availability and blocked periods in real-time.'
    },
    {
      icon: '🔍',
      title: lang === 'fr' ? 'Recherche intelligente' : 'Smart Search',
      desc: lang === 'fr'
        ? 'Les sponsors trouvent des auditeurs qualifiés par domaine d\'expertise avec un calendrier couleur.'
        : 'Sponsors find qualified auditors by expertise domain with a color calendar.'
    },
    {
      icon: '🛡️',
      title: lang === 'fr' ? 'Workflow sécurisé' : 'Secure Workflow',
      desc: lang === 'fr'
        ? 'Pré-réservation, confirmation multi-étapes et validation par les deux parties.'
        : 'Pre-booking, multi-step confirmation and validation by both parties.'
    },
    {
      icon: '⏰',
      title: lang === 'fr' ? 'Debooking contrôlé' : 'Controlled Debooking',
      desc: lang === 'fr'
        ? 'Annulation possible jusqu\'à 3 semaines avant, validée par les deux parties.'
        : 'Cancellation possible up to 3 weeks in advance, validated by both parties.'
    },
    {
      icon: '👥',
      title: lang === 'fr' ? 'Profils auditeurs' : 'Auditor Profiles',
      desc: lang === 'fr'
        ? 'CV téléchargeables, domaines d\'expertise et historique des audits.'
        : 'Downloadable CVs, expertise domains and audit history.'
    },
    {
      icon: '🔔',
      title: lang === 'fr' ? 'Notifications' : 'Notifications',
      desc: lang === 'fr'
        ? 'Alertes in-app et par email pour chaque étape du processus de réservation.'
        : 'In-app and email alerts for each step of the booking process.'
    }
  ];

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo}>
            <div style={styles.dot}></div>
            <span style={styles.logoText}>AuditPlan</span>
          </div>
          
          <div style={styles.headerRight}>
            <LanguageSwitcher />
            <button style={styles.btnSecondary} onClick={() => navigate('/auth')}>
              {lang === 'fr' ? 'Connexion' : 'Login'}
            </button>
            <button style={styles.btnPrimary} onClick={() => navigate('/auth')}>
              {lang === 'fr' ? 'Créer un compte gratuit' : 'Create free account'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.badge}>
            <div style={{...styles.dot, width: '6px', height: '6px'}}></div>
            <span>{lang === 'fr' ? 'Plateforme d\'audit clinique' : 'Clinical Audit Platform'}</span>
          </div>
          
          <h1 style={styles.h1}>
            {lang === 'fr' 
              ? 'Planifiez vos audits cliniques avec précision'
              : 'Plan your clinical audits with precision'}
          </h1>
          
          <p style={styles.heroDesc}>
            {lang === 'fr'
              ? 'La plateforme SaaS qui connecte auditeurs et sponsors pour organiser, réserver et gérer les audits de recherche clinique.'
              : 'The SaaS platform connecting auditors and sponsors to organize, book and manage clinical research audits.'}
          </p>
          
          <div style={styles.btnGroup}>
            <button style={{...styles.btnLarge, ...styles.btnPrimary}} onClick={() => navigate('/auth')}>
              {lang === 'fr' ? 'Créer un compte gratuit' : 'Create free account'}
            </button>
            <button style={{...styles.btnLarge, ...styles.btnOutline}}>
              {lang === 'fr' ? 'En savoir plus' : 'Learn more'}
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={styles.featuresSection}>
        <div style={styles.container}>
          <div style={styles.sectionTitle}>
            <h2 style={styles.h2}>
              {lang === 'fr' ? 'Tout ce dont vous avez besoin' : 'Everything you need'}
            </h2>
            <p style={styles.sectionDesc}>
              {lang === 'fr' 
                ? 'Une suite complète d\'outils pour la gestion des audits de recherche clinique.'
                : 'A complete suite of tools for clinical research audit management.'}
            </p>
          </div>

          <div style={styles.featuresGrid}>
            {features.map((feature, idx) => (
              <div key={idx} style={styles.featureCard}>
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDesc}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section style={styles.stepsSection}>
        <div style={styles.container}>
          <div style={styles.sectionTitle}>
            <h2 style={styles.h2}>
              {lang === 'fr' ? 'Comment ça marche' : 'How it works'}
            </h2>
          </div>

          <div style={styles.stepsGrid}>
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>01</div>
              <h3 style={styles.stepTitle}>
                {lang === 'fr' ? 'Inscription' : 'Registration'}
              </h3>
              <p style={styles.stepDesc}>
                {lang === 'fr'
                  ? 'Créez votre compte en tant qu\'auditeur ou sponsor en quelques secondes.'
                  : 'Create your account as an auditor or sponsor in seconds.'}
              </p>
            </div>

            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>02</div>
              <h3 style={styles.stepTitle}>
                {lang === 'fr' ? 'Configuration' : 'Configuration'}
              </h3>
              <p style={styles.stepDesc}>
                {lang === 'fr'
                  ? 'Auditeurs : ajoutez vos expertises et disponibilités. Sponsors : recherchez par domaine.'
                  : 'Auditors: add your expertise and availability. Sponsors: search by domain.'}
              </p>
            </div>

            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>03</div>
              <h3 style={styles.stepTitle}>
                {lang === 'fr' ? 'Réservation' : 'Booking'}
              </h3>
              <p style={styles.stepDesc}>
                {lang === 'fr'
                  ? 'Processus de booking sécurisé avec confirmation mutuelle à chaque étape.'
                  : 'Secure booking process with mutual confirmation at each step.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaTitle}>
            {lang === 'fr' ? 'Prêt à optimiser vos audits ?' : 'Ready to optimize your audits?'}
          </h2>
          <p style={styles.ctaDesc}>
            {lang === 'fr'
              ? 'Rejoignez la plateforme qui simplifie la planification des audits cliniques.'
              : 'Join the platform that simplifies clinical audit planning.'}
          </p>
          <button style={{...styles.btnLarge, ...styles.btnWhite}} onClick={() => navigate('/auth')}>
            {lang === 'fr' ? 'Créer mon compte' : 'Create my account'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.logo}>
            <div style={styles.dot}></div>
            <span style={styles.logoText}>AuditPlan</span>
          </div>
          <p style={styles.footerText}>
            © 2026 AuditPlan. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
